/**
 * 红包雨：服务器时间与时段计算（纯函数，契约 rer-spec §10.2）
 *
 * 原则：
 * - 只用后端给的绝对毫秒（startMs / endMs / dayEndMs）+ 服务器时间偏移，不依赖手机时区；
 * - 后端字段全部可缺省：非法时段（缺 ms、end ≤ start）直接丢弃，dailyTimes 非法视为「不限次数」，
 *   serverTime 缺失时偏移按 0（退化为本机时间，不崩）。
 */

/** 归一化后的时间段 */
export interface RainSlotTime {
  key: string;
  startMs: number;
  endMs: number;
  /** HH:mm（status 才有；首页 VO 没有） */
  start?: string;
  end?: string;
  /** 后端快照状态（仅在缺 ms 时兜底用，正常以 ms 重新计算） */
  state?: string;
  claimed?: boolean;
}

export type RainPhase = "CLAIMABLE" | "WAITING" | "ENDED";

export interface RainPhaseInput {
  slots: RainSlotTime[];
  nextDaySlots: RainSlotTime[];
  claimedSlotKeys: ReadonlySet<string> | readonly string[];
  claimCount: number;
  /** ≤0 / 非法 = 不限次数 */
  dailyTimes: number;
  /** 今天最后一毫秒（null = 未知，不做跨天处理） */
  dayEndMs: number | null;
}

export interface RainPhaseResult {
  phase: RainPhase;
  /** 当前所在场（WAITING 时可能存在 = 本场已领或今日次数已满） */
  slot?: RainSlotTime;
  /** 下一场（WAITING 的倒计时目标） */
  next?: RainSlotTime;
  /** CLAIMABLE = 本场结束；WAITING = 下一场开始；ENDED = null */
  targetMs: number | null;
}

/** 偏移计算的最大可信范围：超出视为脏数据，回落 0（防止后端字段异常把倒计时算成几十年） */
const MAX_OFFSET_MS = 7 * 24 * 3600 * 1000;

const toFiniteNumber = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** offsetMs = serverTime − 本机时间（收到数据的那一刻算）；serverTime 缺失 / 异常 → 0 */
export function computeClockOffset(serverTime: unknown): number {
  const st = toFiniteNumber(serverTime);
  if (st === null || st <= 0) return 0;
  const offset = st - Date.now();
  return Math.abs(offset) > MAX_OFFSET_MS ? 0 : offset;
}

/** 服务器口径的「现在」 */
export function serverNow(offsetMs: number): number {
  return Date.now() + (Number.isFinite(offsetMs) ? offsetMs : 0);
}

/** "1200" → "12:00"；非 4 位数字原样返回 */
export function slotKeyToHHmm(key: string | null | undefined): string {
  const k = String(key ?? "");
  return /^\d{4}$/.test(k) ? `${k.slice(0, 2)}:${k.slice(2)}` : k;
}

/**
 * 把后端时段数组归一化：丢弃缺 ms / end ≤ start 的项，按 startMs 升序。
 * key 缺失时用 HH:mm 去冒号或 startMs 兜底（只做内部去重用）。
 */
export function normalizeRainSlots(raw: unknown): RainSlotTime[] {
  if (!Array.isArray(raw)) return [];
  const out: RainSlotTime[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const s = item as Record<string, unknown>;
    const startMs = toFiniteNumber(s.startMs);
    const endMs = toFiniteNumber(s.endMs);
    if (startMs === null || endMs === null || endMs <= startMs) continue;
    const start = typeof s.start === "string" ? s.start : undefined;
    const end = typeof s.end === "string" ? s.end : undefined;
    const keyRaw =
      (typeof s.key === "string" && s.key) ||
      (start ? start.replace(":", "") : "") ||
      String(startMs);
    out.push({
      key: keyRaw,
      startMs,
      endMs,
      start,
      end,
      state: typeof s.state === "string" ? s.state : undefined,
      claimed: s.claimed === true,
    });
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}

/** dailyTimes 归一化：非法 / ≤0 → Infinity（不限，防后端缺字段时把所有场判成「次数已满」） */
export function normalizeDailyTimes(v: unknown): number {
  const n = toFiniteNumber(v);
  return n === null || n <= 0 ? Number.POSITIVE_INFINITY : n;
}

/** claimCount 归一化：非法 → 0 */
export function normalizeClaimCount(v: unknown): number {
  const n = toFiniteNumber(v);
  return n === null || n < 0 ? 0 : n;
}

const hasKey = (
  keys: ReadonlySet<string> | readonly string[],
  key: string
): boolean =>
  Array.isArray(keys)
    ? (keys as readonly string[]).includes(key)
    : (keys as ReadonlySet<string>).has(key);

/**
 * 已跨过 dayEndMs 但首页配置 / status 还没刷新时：把 nextDaySlots 当作「今天」，
 * 已领 / 次数清零（新的一天），避免倒计时指向已经开始的场次出现负数。
 */
function resolveDay(input: RainPhaseInput, now: number): RainPhaseInput {
  if (input.dayEndMs !== null && now > input.dayEndMs) {
    return {
      ...input,
      slots: input.nextDaySlots,
      nextDaySlots: [],
      claimedSlotKeys: [],
      claimCount: 0,
      dayEndMs: null,
    };
  }
  return input;
}

/**
 * 浮标 / 按钮阶段（契约 §10.2 原样实现 + 跨天兜底）：
 * - 当前在场、本场未领、今日次数未满 → CLAIMABLE，倒计时到本场结束；
 * - 否则有下一场 → WAITING，倒计时到下一场开始（今日次数已满时直接取明天第一场）；
 * - 都没有 → ENDED（入口消失）。
 */
export function computeRainPhase(
  rawInput: RainPhaseInput,
  now: number
): RainPhaseResult {
  const input = resolveDay(rawInput, now);
  const { slots, nextDaySlots, claimedSlotKeys } = input;
  const cur = slots.find(s => s.startMs <= now && now < s.endMs);
  const dailyFull = input.claimCount >= input.dailyTimes;
  if (cur && !hasKey(claimedSlotKeys, cur.key) && !dailyFull) {
    return { phase: "CLAIMABLE", slot: cur, targetMs: cur.endMs };
  }
  const nextToday = dailyFull ? undefined : slots.find(s => s.startMs > now);
  const next = nextToday ?? nextDaySlots.find(s => s.startMs > now);
  if (next) {
    return { phase: "WAITING", slot: cur, next, targetMs: next.startMs };
  }
  return { phase: "ENDED", slot: cur, targetMs: null };
}

/**
 * 下一个需要重算的时刻：最近的「某场开始 / 某场结束 / 日切（dayEndMs + 1）」。
 * 没有则返回 null。
 */
export function getNextRainBoundary(
  input: Pick<RainPhaseInput, "slots" | "nextDaySlots" | "dayEndMs">,
  now: number
): number | null {
  let best: number | null = null;
  const consider = (ms: number | null | undefined) => {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) return;
    if (ms <= now) return;
    if (best === null || ms < best) best = ms;
  };
  for (const s of input.slots) {
    consider(s.startMs);
    consider(s.endMs);
  }
  for (const s of input.nextDaySlots) {
    consider(s.startMs);
    consider(s.endMs);
  }
  if (input.dayEndMs !== null) consider(input.dayEndMs + 1);
  return best;
}

/** 时段格子状态：以 ms 实时计算为准，缺 ms 时（不会出现，normalize 已过滤）回落后端 state */
export function getSlotState(
  slot: RainSlotTime,
  now: number
): "PAST" | "CURRENT" | "FUTURE" {
  if (slot.endMs <= now) return "PAST";
  if (slot.startMs <= now) return "CURRENT";
  return "FUTURE";
}
